import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useBrandManager } from '@/hooks/useBrandManager';

export interface TaskComment {
  id: string;
  task_id: string;
  task_type: 'foundational' | 'growth' | 'recurring';
  user_id: string;
  brand_id: string;
  comment_text: string;
  mentions: string[];
  created_at: string;
  updated_at: string;
  // Author information from growth_squad table
  author_name?: string;
  author_alias?: string;
}

export const useTaskComments = (taskId: string, taskType: 'foundational' | 'growth' | 'recurring', taskBrandId?: string) => {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();

  const fetchComments = async () => {
    // Use taskBrandId if provided, otherwise fall back to currentBrandId
    const brandIdToUse = taskBrandId || currentBrandId;
    
    if (!taskId || !brandIdToUse) return;

    try {
      setIsLoading(true);
      
      // First get the comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('tasks_comments')
        .select('*')
        .eq('task_id', taskId)
        .eq('task_type', taskType)
        .eq('brand_id', brandIdToUse)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;
      
      // Get unique user IDs from comments
      const userIds = [...new Set((commentsData || []).map(c => c.user_id))];
      
      // Get growth squad info for these users
      const { data: squadData } = await supabase
        .from('growth_squad')
        .select('user_id, name, alias')
        .in('user_id', userIds);
      
      // Create a map for quick lookup
      const squadMap = new Map(squadData?.map(squad => [squad.user_id, squad]) || []);
      
      // Transform the data to include author info
      const commentsWithAuthor = (commentsData || []).map((comment: any) => {
        const squadMember = squadMap.get(comment.user_id);
        
        return {
          ...comment,
          author_name: squadMember?.name,
          author_alias: squadMember?.alias,
        };
      });
      
      setComments(commentsWithAuthor as TaskComment[]);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        title: "Error",
        description: "Failed to fetch comments",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addComment = async (commentText: string, mentions: string[] = []) => {
    // Use taskBrandId if provided, otherwise fall back to currentBrandId
    const brandIdToUse = taskBrandId || currentBrandId;
    
    if (!user || !brandIdToUse || !commentText.trim()) return;

    try {
      const { data, error } = await supabase
        .from('tasks_comments')
        .insert([
          {
            task_id: taskId,
            task_type: taskType,
            user_id: user.id,
            brand_id: brandIdToUse,
            comment_text: commentText.trim(),
            mentions: mentions,
          }
        ])
        .select()
        .single();

      if (error) throw error;

      setComments(prev => [...prev, data as TaskComment]);
      toast({
        title: "Success",
        description: "Comment added successfully",
      });

      return data;
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateComment = async (commentId: string, commentText: string) => {
    if (!commentText.trim()) return;

    try {
      const { data, error } = await supabase
        .from('tasks_comments')
        .update({ comment_text: commentText.trim() })
        .eq('id', commentId)
        .eq('user_id', user?.id)
        .select()
        .single();

      if (error) throw error;

      setComments(prev => 
        prev.map(comment => 
          comment.id === commentId ? { ...comment, ...(data as TaskComment) } : comment
        )
      );

      toast({
        title: "Success",
        description: "Comment updated successfully",
      });
    } catch (error) {
      console.error('Error updating comment:', error);
      toast({
        title: "Error",
        description: "Failed to update comment",
        variant: "destructive",
      });
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('tasks_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user?.id);

      if (error) throw error;

      setComments(prev => prev.filter(comment => comment.id !== commentId));
      toast({
        title: "Success",
        description: "Comment deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: "Error",
        description: "Failed to delete comment",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchComments();
  }, [taskId, taskType, taskBrandId, currentBrandId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    comments,
    isLoading,
    addComment,
    updateComment,
    deleteComment,
    refetch: fetchComments,
  };
};